import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Fab, Paper, Typography, IconButton, TextField, Stack, Chip, CircularProgress, Zoom,
} from '@mui/material';
import { HelpOutline, Close, Send, AutoAwesome } from '@mui/icons-material';
import { api } from '../config/api';

interface Msg { role: 'user' | 'bot'; text: string; }

// Mapea la ruta a un nombre de sección legible (para dar contexto al asistente)
const SECCION: Record<string, string> = {
  '/app': 'Dashboard', '/app/agente': 'Mi Agente', '/app/playground': 'Playground',
  '/app/conexion': 'Conexión WhatsApp', '/app/redes-sociales': 'Instagram y Messenger',
  '/app/plantillas': 'Plantillas', '/app/conversaciones': 'Conversaciones',
  '/app/integraciones': 'Integraciones', '/app/woocommerce': 'WooCommerce',
  '/app/orquestacion': 'Orquestación', '/app/flujos': 'Flujos', '/app/soporte': 'Soporte',
  '/app/pipeline': 'Pipeline', '/app/suscripcion': 'Suscripción',
  '/app/perfil': 'Mi perfil', '/app/faq': 'Preguntas frecuentes', '/app/admin': 'Administración',
};

const AyudaChatbot: React.FC = () => {
  const location = useLocation();
  const [abierto, setAbierto] = useState(false);
  const [mensajes, setMensajes] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [cargando, setCargando] = useState(false);
  const [sugerencias, setSugerencias] = useState<string[]>([]);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [mensajes, cargando]);

  // Al iniciar sesión por primera vez, abrir el chat de ayuda automáticamente
  useEffect(() => {
    const visto = localStorage.getItem('respondo_ayuda_vista');
    if (!visto) {
      setTimeout(() => {
        setAbierto(true);
        setMensajes([{ role: 'bot', text: '¡Hola! 👋 Soy tu asistente de ayuda. Puedo explicarte cómo usar cualquier parte de Respondo: conectar WhatsApp, configurar tu agente, Google Calendar, tu tienda, etc. ¿Qué quieres hacer?' }]);
        localStorage.setItem('respondo_ayuda_vista', '1');
      }, 1200);
    }
  }, []);

  useEffect(() => {
    if (abierto && sugerencias.length === 0) {
      api.get('/ayuda/sugerencias').then((r) => setSugerencias(r.data.sugerencias || [])).catch(() => {});
    }
  }, [abierto, sugerencias.length]);

  // Otras pantallas pueden abrir el asistente con una pregunta (botón "Preguntar al asistente").
  const preguntarRef = useRef<(t: string) => void>(() => {});
  useEffect(() => {
    const h = (e: Event) => {
      const q = (e as CustomEvent<string>).detail;
      setAbierto(true);
      if (q) setTimeout(() => preguntarRef.current(q), 150);
    };
    window.addEventListener('respondo:preguntar-ayuda', h);
    return () => window.removeEventListener('respondo:preguntar-ayuda', h);
  }, []);

  const preguntar = async (texto: string) => {
    const q = texto.trim();
    if (!q || cargando) return;
    setMensajes((m) => [...m, { role: 'user', text: q }]);
    setInput('');
    setCargando(true);
    try {
      const contexto = SECCION[location.pathname] || '';
      const { data } = await api.post('/ayuda/preguntar', { mensaje: q, contexto });
      setMensajes((m) => [...m, { role: 'bot', text: data.respuesta }]);
    } catch {
      setMensajes((m) => [...m, { role: 'bot', text: 'Disculpa, no pude responder ahora. Intenta de nuevo en un momento.' }]);
    } finally {
      setCargando(false);
    }
  };

  preguntarRef.current = preguntar;

  return (
    <>
      {/* Botón flotante de ayuda */}
      <Zoom in={!abierto}>
        <Fab color="primary" onClick={() => setAbierto(true)}
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1300 }}>
          <HelpOutline />
        </Fab>
      </Zoom>

      {/* Ventana del chat */}
      {abierto && (
        <Paper elevation={8} sx={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 1300,
          width: 360, maxWidth: '92vw', height: 520, maxHeight: '78vh',
          display: 'flex', flexDirection: 'column', borderRadius: 3, overflow: 'hidden',
        }}>
          {/* Header */}
          <Box sx={{ background: 'linear-gradient(135deg,#075E54,#25D366)', color: '#fff', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <AutoAwesome fontSize="small" />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>Asistente de Ayuda</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Pregúntame cómo usar Respondo</Typography>
              </Box>
            </Stack>
            <IconButton size="small" onClick={() => setAbierto(false)} sx={{ color: '#fff' }}><Close fontSize="small" /></IconButton>
          </Box>

          {/* Mensajes */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, bgcolor: '#F0F2F5', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {mensajes.length === 0 && (
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 2 }}>
                Hazme una pregunta sobre la plataforma 👇
              </Typography>
            )}
            {mensajes.map((m, i) => (
              <Box key={i} sx={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <Paper sx={{ p: 1.2, px: 1.6, maxWidth: '85%', bgcolor: m.role === 'user' ? '#DCF8C6' : '#fff', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.text}</Typography>
                </Paper>
              </Box>
            ))}
            {cargando && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Paper sx={{ p: 1.2, px: 2, bgcolor: '#fff', borderRadius: 2 }}><CircularProgress size={16} /></Paper>
              </Box>
            )}
            {/* Sugerencias (solo si no hay conversación avanzada) */}
            {mensajes.length <= 1 && sugerencias.length > 0 && (
              <Stack spacing={0.8} sx={{ mt: 1 }}>
                {sugerencias.map((s) => (
                  <Chip key={s} label={s} variant="outlined" size="small" onClick={() => preguntar(s)}
                    sx={{ justifyContent: 'flex-start', cursor: 'pointer', bgcolor: '#fff' }} />
                ))}
              </Stack>
            )}
            <div ref={endRef} />
          </Box>

          {/* Input */}
          <Box sx={{ p: 1.2, display: 'flex', gap: 1, borderTop: '1px solid #e0e0e0', bgcolor: '#fff' }}>
            <TextField fullWidth size="small" placeholder="Escribe tu pregunta..." value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') preguntar(input); }} />
            <IconButton color="primary" onClick={() => preguntar(input)} disabled={cargando}><Send /></IconButton>
          </Box>
        </Paper>
      )}
    </>
  );
};

export default AyudaChatbot;
